import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { computeFileHash, getPresignedUrl, uploadToS3 } from "../api";

const STATUS = {
  IDLE: "idle",
  HASHING: "hashing",
  REQUESTING: "requesting",
  UPLOADING: "uploading",
  DONE: "done",
  ERROR: "error",
  DUPLICATE: "duplicate",
};

export default function FileUploader({ fileType, onUploadComplete }) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [logId, setLogId] = useState(null);

  const processFile = useCallback(
    async (file) => {
      setStatus(STATUS.HASHING);
      setMessage("Computing file hash...");
      setProgress(0);

      try {
        const hash = await computeFileHash(file);

        setStatus(STATUS.REQUESTING);
        setMessage("Checking for duplicates and requesting upload URL...");

        let presigned;
        try {
          presigned = await getPresignedUrl(file.name, fileType, hash);
        } catch (err) {
          if (err.response?.status === 409) {
            setStatus(STATUS.DUPLICATE);
            setMessage(
              `This file has already been processed. ${err.response.data.detail}`
            );
            return;
          }
          throw err;
        }

        setLogId(presigned.log_id);
        setStatus(STATUS.UPLOADING);
        setMessage("Uploading to S3...");

        await uploadToS3(presigned.upload_url, file, setProgress);

        setStatus(STATUS.DONE);
        setMessage(`Upload complete! Processing log ID: ${presigned.log_id}`);
        if (onUploadComplete) onUploadComplete(presigned.log_id);
      } catch (err) {
        setStatus(STATUS.ERROR);
        setMessage(
          err.response?.data?.detail || err.message || "Upload failed"
        );
      }
    },
    [fileType, onUploadComplete]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        processFile(acceptedFiles[0]);
      }
    },
    [processFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    maxFiles: 1,
    disabled: status === STATUS.UPLOADING || status === STATUS.HASHING,
  });

  const borderColor =
    status === STATUS.ERROR || status === STATUS.DUPLICATE
      ? "#e53e3e"
      : status === STATUS.DONE
      ? "#38a169"
      : isDragActive
      ? "#3182ce"
      : "#cbd5e0";

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <h3 style={{ textTransform: "capitalize", marginBottom: "0.5rem" }}>
        {fileType} File
      </h3>

      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${borderColor}`,
          borderRadius: "8px",
          padding: "2rem",
          textAlign: "center",
          cursor: "pointer",
          background: isDragActive ? "#ebf8ff" : "#f7fafc",
          transition: "border-color 0.2s",
        }}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the CSV file here...</p>
        ) : (
          <p>Drag & drop a {fileType} CSV file here, or click to select</p>
        )}
      </div>

      {status !== STATUS.IDLE && (
        <div style={{ marginTop: "0.75rem" }}>
          {status === STATUS.UPLOADING && (
            <div
              style={{
                background: "#e2e8f0",
                borderRadius: "4px",
                overflow: "hidden",
                height: "8px",
                marginBottom: "0.5rem",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  background: "#3182ce",
                  height: "100%",
                  transition: "width 0.2s",
                }}
              />
            </div>
          )}
          <p
            style={{
              color:
                status === STATUS.ERROR || status === STATUS.DUPLICATE
                  ? "#e53e3e"
                  : status === STATUS.DONE
                  ? "#38a169"
                  : "#4a5568",
              fontSize: "0.875rem",
            }}
          >
            {message}
          </p>
        </div>
      )}
    </div>
  );
}
